import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LabelList, ReferenceLine, Cell, Legend, Tooltip } from 'recharts';

// Add global styles to remove focus outlines
const chartStyles = `
  .recharts-wrapper,
  .recharts-surface,
  .recharts-bar-rectangle,
  .recharts-rectangle {
    outline: none !important;
  }
  .recharts-wrapper:focus,
  .recharts-surface:focus,
  .recharts-wrapper *:focus {
    outline: none !important;
  }
`;

// Custom Tooltip Component showing all segments for the hovered bar
const CustomTooltip = ({ active, payload, label, coordinate, isCustom, customise, stackKeys }) => {
  if (active && payload && payload.length && coordinate) {
    // Filter out segments with zero values and reverse the stack order (top to bottom)
    const validSegments = payload
      .filter(item => item.value > 0)
      .sort((a, b) => {
        // Sort by the reverse order in stackKeys array (top to bottom of stack)
        const indexA = stackKeys.indexOf(a.dataKey);
        const indexB = stackKeys.indexOf(b.dataKey);
        return indexB - indexA; // Reversed
      });
    
    if (validSegments.length === 0) return null;

    return (
      <div 
        className="rounded-lg shadow-lg px-3 py-3 pointer-events-none"
        style={{
          backgroundColor: 'var(--gray-bg-normal)',
          border: '1px solid var(--border-underline)',
          transform: 'translate(10px, -50%)',
          minWidth: '200px',
        }}
      >
        {/* Show all segments */}
        <div className="space-y-1">
          {validSegments.map((segment, index) => {
            const { dataKey, value, color } = segment;
            const displayName = isCustom ? customise(dataKey) : dataKey;
            
            return (
              <div key={dataKey} className="flex items-center justify-between gap-3">
                {/* Left side: Color indicator and label */}
                <div className="flex items-center gap-2 min-w-0">
                  <div 
                    className="w-3 h-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span 
                    className="text-xs truncate"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {displayName}
                  </span>
                </div>
                
                {/* Right side: Value */}
                <span 
                  className="text-xs flex-shrink-0"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {value}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return null;
};

const StackedVerticalBarChart = ({ 
  data = [], 
  xAxisLabel = 'X Axis', 
  yAxisLabel = 'Y Axis', 
  stackKeys = [], // Array of keys to stack, e.g., ['type1', 'type2', 'type3']
  colors = [{ barColor: '#003C73', textColor: '#fff' }, { barColor: '#76C8E8', textColor: '#171717' }],
  barSize = 40,
  height = 400,
  showReferenceLine = false,
  referenceLineValue = 50,
  domain = [0, 100],
  ticks = [0, 20, 40, 60, 80, 100],
  valueFormatter = (value) => value,
  visibleBars = 16,
  showLegend = true,
  isCustom=false,
  customise
}) => {
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(0);

  // Calculate visible data
  const visibleData = data.slice(visibleStartIndex, visibleStartIndex + visibleBars);
  const showScrollbar = data.length > visibleBars;

  // Calculate dynamic bar size based on number of bars
  const calculateDynamicBarSize = (numBars) => {
    if (numBars === 1) return 177;
    if (numBars === 2) return 177;
    if (numBars === 3) return 177;
    if (numBars === 4) return 139;
    if (numBars === 5) return 104;
    // For 6 or more bars, use the provided barSize prop
    return barSize;
  };

  const dynamicBarSize = calculateDynamicBarSize(visibleData.length);

  // Calculate dynamic domain from actual data (sum of stacked values)
  const calculateMaxStackedValue = () => {
    if (!Array.isArray(data) || data.length === 0) return 100;
    
    const maxStacked = data.reduce((max, item) => {
      const stackedSum = stackKeys.reduce((sum, key) => {
        return sum + (Number(item[key]) || 0);
      }, 0);
      return Math.max(max, stackedSum);
    }, 0);
    
    return maxStacked;
  };

  const maxVal = calculateMaxStackedValue();
  const roundedMax = Math.ceil(maxVal * 1.1); // Add 10% padding
  const computedDomain = [0, roundedMax];

  // Calculate spacer width for horizontal scrolling
  const spacerWidth = viewportWidth > 0
    ? Math.max(viewportWidth, data.length * (viewportWidth / visibleBars))
    : '100%';

  useEffect(() => {
    const updateWidth = () => {
      if (scrollContainerRef.current) {
        setViewportWidth(scrollContainerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Handle scroll for pagination
  const handleScroll = () => {
    if (!scrollContainerRef.current || !showScrollbar) return;
    
    const scrollLeft = scrollContainerRef.current.scrollLeft;
    const containerWidth = scrollContainerRef.current.offsetWidth;
    const scrollWidth = scrollContainerRef.current.scrollWidth;
    
    const scrollPercentage = scrollLeft / (scrollWidth - containerWidth);
    const maxStartIndex = Math.max(0, data.length - visibleBars);
    const newStartIndex = Math.round(scrollPercentage * maxStartIndex);
    
    setVisibleStartIndex(newStartIndex);
  };

  // Custom tick renderer for X-axis with improved multi-line support
  const renderXAxisTick = (props) => {
    const { x, y, payload } = props;
    const text = payload?.value || '';
    const words = text.split(' ');
    
    // More aggressive line splitting for tighter spaces (max 2 words per line)
    const lines = [];
    const wordsPerLine = 2;
    for (let i = 0; i < words.length; i += wordsPerLine) {
      lines.push(words.slice(i, i + wordsPerLine).join(' '));
    }
    
    // Reduce line height and font size for better fit
    const lineHeight = 10;
    const fontSize = 10;
    const startY = y + 5;
    
    return (
      <text x={x} textAnchor="middle" fill="var(--text-primary)" fontSize={fontSize}>
        {lines.map((line, index) => (
          <tspan key={index} x={x} y={startY + (index * lineHeight)}>
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  // Custom label component to show individual values inside bars
  const renderCustomLabel = (props, dataKey, keyIndex) => {
    const { x, y, width, height, value, index } = props;
    
    // Get the individual value for this segment
    let individualValue = 0;
    if (visibleData && visibleData[index]) {
      const dataPoint = visibleData[index];
      individualValue = dataPoint[dataKey] || 0;
    }
    
    // Don't show label if value is 0 or segment is too small
    if (!individualValue || individualValue === 0 || height < 20) return null;
    
    // Get text color from colors array
    const colorConfig = colors[keyIndex % colors.length];
    const textColor = typeof colorConfig === 'object' ? colorConfig.textColor : '#fff';
    
    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        fill={textColor}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="10"
        fontWeight="500"
      >
        {individualValue}
      </text>
    );
  };


  // Custom legend renderer
  const renderLegend = (props) => {
    const { payload } = props;
    
    return (
      <div className="flex flex-wrap justify-center gap-4 px-4">
        {payload.map((entry, index) => (
          <div key={`legend-${index}`} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-xs text-textPrimary">{ isCustom ? customise(entry?.value):  entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-[400px] flex items-center justify-center text-textPrimary">
        No data available
      </div>
    );
  }

  return (
    <>
      <style>{chartStyles}</style>
      <div className="w-full h-[500px] flex flex-col">
        <div 
          ref={scrollContainerRef}
          className={`flex-1 ${showScrollbar ? 'overflow-x-auto' : ''}`}
          style={{ 
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e0 #f7fafc',
            outline: 'none'
          }}
          onScroll={handleScroll}
          tabIndex={-1}
        >
          <div style={{ 
            minWidth: showScrollbar ? `${Math.max(800, data.length * 50)}px` : '100%', 
            width: showScrollbar ? `${Math.max(800, data.length * 50)}px` : '100%',
            height: '100%'
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={visibleData}
                margin={{
                  top: 20,
                  right: 6,
                  left: 6,
                  bottom: -40,
                }}
                barCategoryGap="6%"
              >
                <XAxis 
                  dataKey="name"
                  tick={renderXAxisTick}
                  height={70}
                  interval={0}
                  tickMargin={5}
                />
                <YAxis 
                  domain={computedDomain}
                  label={{ 
                    value: yAxisLabel, 
                    angle: -90, 
                    position: 'insideLeft',
                    offset: 4,
                    style: { textAnchor: 'middle', fontSize: 12, fill: 'var(--text-primary)' }
                  }}
                  tick={{ fontSize: 10, fill: 'var(--text-primary)' }}
                />
                
                {/* Custom Tooltip */}
                <Tooltip 
                  content={<CustomTooltip isCustom={isCustom} customise={customise} stackKeys={stackKeys} />}
                  cursor={{ fill: 'rgba(59, 130, 246, 0.1)', stroke: 'none' }}
                  isAnimationActive={false}
                />

                {showReferenceLine && (
                  <ReferenceLine y={referenceLineValue} stroke="#666" strokeDasharray="3 3" />
                )}
                
                {/* Render stacked bars with labels */}
                {stackKeys.map((key, keyIndex) => {
                  const colorConfig = colors[keyIndex % colors.length];
                  const barColor = typeof colorConfig === 'object' ? colorConfig.barColor : colorConfig;
                  
                  return (
                    <Bar 
                      key={key}
                      dataKey={key}
                      stackId="stack"
                      fill={barColor}
                      maxBarSize={dynamicBarSize}
                    >
                      <LabelList 
                        content={(props) => renderCustomLabel(props, key, keyIndex)}
                      />
                    </Bar>
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Fixed X-axis label */}
        <div className="h-10 flex items-center justify-center bg-grayBgNormal">
          <div className="text-xs text-textPrimary font-medium">
            {xAxisLabel}
          </div>
        </div>
        
        {/* Legend below X-axis label */}
        {showLegend && (
          <div className="h-10 flex items-center justify-center bg-grayBgNormal">
            {renderLegend({ payload: stackKeys.map((key, index) => {
              const colorConfig = colors[index % colors.length];
              const barColor = typeof colorConfig === 'object' ? colorConfig.barColor : colorConfig;
              return { 
                value: key, 
                color: barColor 
              };
            }) })}
          </div>
        )}
      </div>
    </>
  );
};

export default StackedVerticalBarChart;
